import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BriefcaseIcon, WrenchIcon, StarIcon, CarIcon, DumbbellIcon, HeartIcon, MessageCircleIcon, Share2Icon, GiftIcon, XIcon, FlameIcon, GamepadIcon } from 'lucide-react';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number;
  avatar: string;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const PATH_COLORS = [{
  name: 'School',
  glow: '#a855f7',
  light: '#c084fc',
  dark: '#7e22ce',
  tile: '#e9d5ff',
  text: '#6b21a8',
  icon: GraduationCapIcon
}, {
  name: 'Workforce',
  glow: '#ec4899',
  light: '#f472b6',
  dark: '#be185d',
  tile: '#fbcfe8',
  text: '#9d174d',
  icon: BriefcaseIcon
}, {
  name: 'Trade',
  glow: '#f97316',
  light: '#fb923c',
  dark: '#c2410c',
  tile: '#fed7aa',
  text: '#9a3412',
  icon: WrenchIcon
}, {
  name: 'Self-Made',
  glow: '#14b8a6',
  light: '#2dd4bf',
  dark: '#0f766e',
  tile: '#99f6e4',
  text: '#115e59',
  icon: StarIcon
}];
// Lane geometry — each lane is a trapezoid converging to a vanishing point.
// Coordinates in % of viewport width/height.
// Vanishing point: x=50%, y=42%
const VP_X = 50;
const VP_Y = 42;
// Lane bottom edges (x positions at bottom of screen, in vw %)
const LANE_BOTTOMS = [{
  left: 4,
  right: 26
}, {
  left: 26,
  right: 50
}, {
  left: 50,
  right: 74
}, {
  left: 74,
  right: 96
} // Self-Made - teal
];
// Lane top widths at vanishing point — they all converge to ~3% wide there
const LANE_TOPS = [{
  left: 47.5,
  right: 49
}, {
  left: 49,
  right: 50
}, {
  left: 50,
  right: 51
}, {
  left: 51,
  right: 52.5
}];
const TILES_BY_LANE: Array<Array<{
  icon: ComponentType<any>;
  label: string;
  amount: string;
  positive: boolean;
}>> = [[{
  icon: GraduationCapIcon,
  label: 'Study Hard\nPay Off',
  amount: '+$150',
  positive: true
}, {
  icon: GraduationCapIcon,
  label: 'Kid Needed\nSchool Supplies',
  amount: '-$80',
  positive: false
}, {
  icon: GraduationCapIcon,
  label: 'Aced the Exam',
  amount: '+$60',
  positive: true
}], [{
  icon: BriefcaseIcon,
  label: 'Got a\nPart-Time Job',
  amount: '+$120',
  positive: true
}, {
  icon: BriefcaseIcon,
  label: 'No Income\nThis Week',
  amount: '-$100',
  positive: false
}, {
  icon: BriefcaseIcon,
  label: 'Promotion!',
  amount: '+$90',
  positive: true
}], [{
  icon: CarIcon,
  label: 'Car Broke\nDown',
  amount: '-$200',
  positive: false
}, {
  icon: WrenchIcon,
  label: 'Side Hustle\nSuccess',
  amount: '+$300',
  positive: true
}, {
  icon: WrenchIcon,
  label: 'Tool Upgrade',
  amount: '-$50',
  positive: false
}], [{
  icon: DumbbellIcon,
  label: 'Invest in\nYourself',
  amount: '+$250',
  positive: true
}, {
  icon: HeartIcon,
  label: 'Got a\nCompliment\nFrom a Stranger',
  amount: '+$0',
  positive: true
}, {
  icon: StarIcon,
  label: 'Manifest\nWin',
  amount: '+$180',
  positive: true
}]];
// Helper: linearly interpolate position along a lane based on t (0 = bottom/foreground, 1 = vanishing point)
function laneXAt(laneIdx: number, side: 'left' | 'right', t: number) {
  const bottom = LANE_BOTTOMS[laneIdx][side];
  const top = LANE_TOPS[laneIdx][side];
  return bottom + (top - bottom) * t;
}
function laneYAt(t: number) {
  // bottom is 100, top (vanishing) is VP_Y
  return 100 + (VP_Y - 100) * t;
}
function laneCenter(laneIdx: number, t: number) {
  const x = (laneXAt(laneIdx, 'left', t) + laneXAt(laneIdx, 'right', t)) / 2;
  const y = laneYAt(t);
  return {
    x,
    y
  };
}
function laneWidth(laneIdx: number, t: number) {
  return laneXAt(laneIdx, 'right', t) - laneXAt(laneIdx, 'left', t);
}
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  // Tile positions along the lane (t values from foreground -> horizon)
  const tilePositions = [0.05, 0.32, 0.58];
  return <div className="fixed inset-0 z-30 overflow-hidden bg-black select-none" data-id="element-194">
      {/* ============ BACKGROUND: Cityscape + sunset ============ */}
      <div className="absolute inset-0" data-id="element-195">
        {/* Sky gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #1a1340 0%, #2a1a4a 25%, #3d2356 40%, #6b3a5a 55%, #c97a5a 65%, #1a1340 75%, #0a0820 100%)'
      }} data-id="element-196" />
        {/* Sunset glow at vanishing point */}
        <div className="absolute" style={{
        left: `${VP_X}%`,
        top: `${VP_Y}%`,
        width: '600px',
        height: '600px',
        transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(255,200,120,0.6) 0%, rgba(255,140,80,0.3) 25%, transparent 60%)',
        filter: 'blur(20px)'
      }} data-id="element-197" />
        {/* Sun core */}
        <div className="absolute w-3 h-3 rounded-full" style={{
        left: `${VP_X}%`,
        top: `${VP_Y}%`,
        transform: 'translate(-50%, -50%)',
        background: '#fff5d6',
        boxShadow: '0 0 30px 15px #ffd89b, 0 0 80px 30px rgba(255,180,100,0.6)'
      }} data-id="element-198" />

        {/* City silhouettes — far buildings near horizon */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-199">
          {/* distant buildings */}
          <g fill="#0d0a26" opacity="0.95" data-id="element-200">
            <rect x="10" y="28" width="6" height="14" data-id="element-201" />
            <rect x="16" y="32" width="4" height="10" data-id="element-202" />
            <rect x="20" y="25" width="7" height="17" data-id="element-203" />
            <rect x="27" y="30" width="5" height="12" data-id="element-204" />
            <rect x="32" y="22" width="8" height="20" data-id="element-205" />
            <rect x="40" y="28" width="4" height="14" data-id="element-206" />
            <rect x="56" y="26" width="5" height="16" data-id="element-207" />
            <rect x="61" y="22" width="7" height="20" data-id="element-208" />
            <rect x="68" y="29" width="5" height="13" data-id="element-209" />
            <rect x="73" y="25" width="6" height="17" data-id="element-210" />
            <rect x="79" y="30" width="4" height="12" data-id="element-211" />
            <rect x="83" y="27" width="6" height="15" data-id="element-212" />
          </g>
          {/* tiny window lights */}
          <g fill="#ffd89b" opacity="0.7" data-id="element-213">
            {Array.from({
            length: 30
          }).map((_, i) => {
            const x = 10 + i * 2.7 % 80;
            const y = 28 + i * 7 % 12;
            return <rect key={i} x={x} y={y} width="0.3" height="0.3" data-id="element-214" />;
          })}
          </g>
        </svg>

        {/* Tree silhouettes on sides */}
        <div className="absolute left-0 top-0 bottom-0 w-[18%] pointer-events-none" data-id="element-215">
          <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 0% 60%, rgba(15,30,20,0.95) 0%, rgba(15,30,20,0.7) 30%, transparent 60%)'
        }} data-id="element-216" />
          {/* tree blobs */}
          <div className="absolute left-[-40px] top-[20%] w-48 h-48 rounded-full bg-[#0a1a12] blur-md" data-id="element-217" />
          <div className="absolute left-[-30px] top-[45%] w-56 h-56 rounded-full bg-[#0a1a12] blur-md" data-id="element-218" />
          <div className="absolute left-[-20px] top-[70%] w-64 h-64 rounded-full bg-[#0a1a12] blur-lg" data-id="element-219" />
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-[18%] pointer-events-none" data-id="element-220">
          <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 100% 60%, rgba(15,30,20,0.95) 0%, rgba(15,30,20,0.7) 30%, transparent 60%)'
        }} data-id="element-221" />
          <div className="absolute right-[-40px] top-[20%] w-48 h-48 rounded-full bg-[#0a1a12] blur-md" data-id="element-222" />
          <div className="absolute right-[-30px] top-[45%] w-56 h-56 rounded-full bg-[#0a1a12] blur-md" data-id="element-223" />
          <div className="absolute right-[-20px] top-[70%] w-64 h-64 rounded-full bg-[#0a1a12] blur-lg" data-id="element-224" />
        </div>

        {/* Road / asphalt under lanes */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(180deg, transparent 42%, #1a1525 45%, #0d0a1a 100%)'
      }} data-id="element-225" />
      </div>

      {/* ============ THE 4 GLOWING LANES (SVG) ============ */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-226">
        <defs data-id="element-227">
          {PATH_COLORS.map((c, i) => <linearGradient key={i} id={`lane-grad-${i}`} x1="0%" y1="100%" x2="0%" y2="0%" data-id="element-228">
              <stop offset="0%" stopColor={c.light} stopOpacity="0.95" data-id="element-229" />
              <stop offset="60%" stopColor={c.glow} stopOpacity="0.7" data-id="element-230" />
              <stop offset="100%" stopColor={c.dark} stopOpacity="0.3" data-id="element-231" />
            </linearGradient>)}
          <filter id="lane-blur" x="-20%" y="-20%" width="140%" height="140%" data-id="element-232">
            <feGaussianBlur stdDeviation="0.4" data-id="element-233" />
          </filter>
        </defs>

        {/* Lane fills */}
        {PATH_COLORS.map((c, i) => {
        const bL = LANE_BOTTOMS[i].left;
        const bR = LANE_BOTTOMS[i].right;
        const tL = LANE_TOPS[i].left;
        const tR = LANE_TOPS[i].right;
        return <g key={i} data-id="element-234">
              {/* outer glow stroke */}
              <polygon points={`${bL},100 ${bR},100 ${tR},${VP_Y} ${tL},${VP_Y}`} fill={`url(#lane-grad-${i})`} opacity="0.85" data-id="element-235" />
              {/* bright neon edges */}
              <line x1={bL} y1="100" x2={tL} y2={VP_Y} stroke={c.light} strokeWidth="0.4" opacity="0.9" style={{
            filter: `drop-shadow(0 0 6px ${c.glow})`
          }} data-id="element-236" />
              <line x1={bR} y1="100" x2={tR} y2={VP_Y} stroke={c.light} strokeWidth="0.4" opacity="0.9" style={{
            filter: `drop-shadow(0 0 6px ${c.glow})`
          }} data-id="element-237" />
              {/* dashed center line */}
              <line x1={(bL + bR) / 2} y1="100" x2={(tL + tR) / 2} y2={VP_Y} stroke="#ffffff" strokeWidth="0.15" strokeDasharray="0.8 1.2" opacity="0.5" data-id="element-238" />
            </g>;
      })}
      </svg>

      {/* ============ TILES ON LANES ============ */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-239">
        {TILES_BY_LANE.map((tiles, laneIdx) => {
        const color = PATH_COLORS[laneIdx];
        return tiles.map((tile, tIdx) => {
          const t = tilePositions[tIdx];
          const center = laneCenter(laneIdx, t);
          const w = laneWidth(laneIdx, t) * 0.85; // tile width as % of viewport
          // scale tile height proportionally to perceived distance
          const scale = 1 - t * 0.7;
          const heightVh = 14 * scale;
          const Icon = tile.icon;
          const isPositive = tile.positive;
          return <motion.div key={`${laneIdx}-${tIdx}`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3 + tIdx * 0.15 + laneIdx * 0.05,
            duration: 0.5
          }} className="absolute rounded-2xl flex flex-col items-center justify-center text-center" style={{
            left: `${center.x}%`,
            top: `${center.y}%`,
            width: `${w}vw`,
            height: `${heightVh}vh`,
            transform: 'translate(-50%, -50%)',
            background: color.tile,
            boxShadow: `0 ${4 * scale}px ${20 * scale}px rgba(0,0,0,0.4), 0 0 ${30 * scale}px ${color.glow}66, inset 0 1px 0 rgba(255,255,255,0.6)`,
            padding: `${0.6 * scale}rem`,
            border: `1px solid ${color.light}`
          }} data-id="element-240">
                <Icon style={{
              width: `${22 * scale}px`,
              height: `${22 * scale}px`,
              color: color.text,
              marginBottom: `${4 * scale}px`
            }} data-id="element-241" />
                <div className="font-bold leading-tight whitespace-pre-line" style={{
              color: color.text,
              fontSize: `${Math.max(8, 13 * scale)}px`
            }} data-id="element-242">
                  {tile.label}
                </div>
                <div className="font-bold mt-1" style={{
              color: isPositive ? color.text : '#dc2626',
              fontSize: `${Math.max(9, 15 * scale)}px`
            }} data-id="element-243">
                  {tile.amount}
                </div>
              </motion.div>;
        });
      })}
      </div>

      {/* ============ PLAYER AVATARS — at start of their lane ============ */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-244">
        {[0, 1, 2, 3].map(laneIdx => {
        const lanePlayers = players.filter(p => p.pathIndex === laneIdx);
        if (lanePlayers.length === 0) return null;
        const center = laneCenter(laneIdx, 0.02);
        return <div key={laneIdx} className="absolute flex gap-1" style={{
          left: `${center.x}%`,
          top: `${center.y - 8}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-245">
              {lanePlayers.map(p => <motion.div key={p.id} initial={{
            scale: 0,
            y: -20
          }} animate={{
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className="w-10 h-10 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-lg" style={{
            backgroundColor: p.color
          }} title={p.name} data-id="element-246">
                  {p.avatar}
                </motion.div>)}
            </div>;
      })}
      </div>

      {/* ============ 3D DICE + PURPLE PAWN (foreground center) ============ */}
      <div className="absolute left-1/2 bottom-[14%] -translate-x-1/2 pointer-events-none flex items-end gap-6 z-20" data-id="element-247">
        {/* Dice */}
        <motion.div initial={{
        rotate: -20,
        y: 30,
        opacity: 0
      }} animate={{
        rotate: -8,
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.8,
        type: 'spring',
        stiffness: 150
      }} className="relative" style={{
        width: 110,
        height: 110
      }} data-id="element-248">
          <div className="absolute inset-0 rounded-2xl" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #c8c8c8 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.6), inset -8px -8px 16px rgba(0,0,0,0.15), inset 4px 4px 8px rgba(255,255,255,0.8)',
          transform: 'perspective(400px) rotateX(15deg) rotateY(-15deg)'
        }} data-id="element-249">
            {/* dice dots — pattern of 5 */}
            <div className="absolute inset-0 p-4" data-id="element-250">
              <div className="relative w-full h-full" data-id="element-251">
                <div className="absolute top-0 left-0 w-3 h-3 rounded-full bg-[#1a1a1a]" data-id="element-252" />
                <div className="absolute top-0 right-0 w-3 h-3 rounded-full bg-[#1a1a1a]" data-id="element-253" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#1a1a1a]" data-id="element-254" />
                <div className="absolute bottom-0 left-0 w-3 h-3 rounded-full bg-[#1a1a1a]" data-id="element-255" />
                <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#1a1a1a]" data-id="element-256" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Purple Pawn */}
        <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 1,
        type: 'spring',
        stiffness: 150
      }} data-id="element-257">
          <svg width="80" height="120" viewBox="0 0 80 120" data-id="element-258">
            <defs data-id="element-259">
              <radialGradient id="pawn-grad" cx="35%" cy="30%" r="70%" data-id="element-260">
                <stop offset="0%" stopColor="#e9a5ff" data-id="element-261" />
                <stop offset="40%" stopColor="#a855f7" data-id="element-262" />
                <stop offset="100%" stopColor="#4a1a6e" data-id="element-263" />
              </radialGradient>
              <radialGradient id="pawn-base-grad" cx="50%" cy="30%" r="70%" data-id="element-264">
                <stop offset="0%" stopColor="#c084fc" data-id="element-265" />
                <stop offset="100%" stopColor="#3b0a5e" data-id="element-266" />
              </radialGradient>
            </defs>
            {/* base */}
            <ellipse cx="40" cy="110" rx="32" ry="8" fill="url(#pawn-base-grad)" data-id="element-267" />
            <path d="M 12 102 Q 12 96 18 92 L 62 92 Q 68 96 68 102 L 68 108 Q 68 112 60 112 L 20 112 Q 12 112 12 108 Z" fill="url(#pawn-base-grad)" data-id="element-268" />
            {/* neck */}
            <path d="M 28 92 L 30 70 L 50 70 L 52 92 Z" fill="url(#pawn-grad)" data-id="element-269" />
            {/* head */}
            <circle cx="40" cy="50" r="22" fill="url(#pawn-grad)" data-id="element-270" />
            {/* highlight */}
            <ellipse cx="32" cy="42" rx="6" ry="9" fill="white" opacity="0.35" data-id="element-271" />
          </svg>
        </motion.div>
      </div>

      {/* ============ TOP HEADER — Wordmark ============ */}
      <div className="absolute top-0 left-0 right-0 pt-4 pb-3 z-40 pointer-events-none" data-id="element-272">
        <div className="flex flex-col items-center" data-id="element-273">
          <motion.div initial={{
          opacity: 0,
          y: -20,
          scale: 0.9
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.6
        }} className="relative px-6 py-2 rounded-2xl" style={{
          background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(236,72,153,0.2))',
          boxShadow: '0 0 40px rgba(236,72,153,0.5)'
        }} data-id="element-274">
            <h1 className="text-4xl md:text-5xl font-bold leading-none text-center" style={{
            fontFamily: '"Dancing Script", cursive',
            background: 'linear-gradient(180deg, #fbbf24 0%, #f97316 30%, #ec4899 60%, #a855f7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: '0 0 30px rgba(236,72,153,0.4)',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
          }} data-id="element-275">
              Trippin'
            </h1>
            <h1 className="text-3xl md:text-4xl font-bold leading-none text-center -mt-1" style={{
            fontFamily: '"Dancing Script", cursive',
            background: 'linear-gradient(180deg, #67e8f9 0%, #06b6d4 50%, #0891b2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
          }} data-id="element-276">
              Through Life
            </h1>
          </motion.div>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="mt-2 px-4 py-1 rounded-full border border-amber-300/60 bg-black/40 backdrop-blur-sm" data-id="element-277">
            <p className="text-[10px] md:text-xs font-bold tracking-wider text-amber-200 uppercase" data-id="element-278">
              Roll the dice. Make choices. Build your life.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ============ TOP-LEFT: Live profile chip ============ */}
      <div className="absolute top-3 left-3 z-40 pointer-events-none" data-id="element-279">
        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-full pr-3 py-1 pl-1" data-id="element-280">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-400 to-purple-600 border-2 border-white" data-id="element-281" />
          <div className="text-white" data-id="element-282">
            <div className="text-xs font-bold leading-none" data-id="element-283">
              Trippin' Through Life
            </div>
            <div className="flex items-center gap-1 mt-0.5" data-id="element-284">
              <HeartIcon className="w-3 h-3 text-pink-400 fill-pink-400" data-id="element-285" />
              <span className="text-[10px] font-semibold" data-id="element-286">12.8K</span>
            </div>
          </div>
          <button className="ml-2 px-3 py-1 rounded-full bg-pink-500 text-white text-[10px] font-bold pointer-events-auto" data-id="element-287">
            + Follow
          </button>
        </div>
        <div className="flex items-center gap-3 mt-2 ml-1 text-white text-[11px] font-bold" data-id="element-288">
          <span className="flex items-center gap-1" data-id="element-289">
            <FlameIcon className="w-3 h-3 text-orange-400" data-id="element-290" /> Daily Ranking
          </span>
          <span className="flex items-center gap-1" data-id="element-291">
            <GamepadIcon className="w-3 h-3 text-purple-400" data-id="element-292" /> Gaming
          </span>
        </div>
      </div>

      {/* ============ TOP-RIGHT: TikTok LIVE + close ============ */}
      <div className="absolute top-3 right-3 z-40 pointer-events-none flex items-center gap-3" data-id="element-293">
        <span className="text-white text-xs font-bold" data-id="element-294">1.2K</span>
        <XIcon className="w-5 h-5 text-white" data-id="element-295" />
      </div>
      <div className="absolute top-14 right-3 z-40 pointer-events-none" data-id="element-296">
        <div className="bg-black px-2 py-1 rounded text-white text-[10px] font-black flex items-center gap-1" data-id="element-297">
          TikTok <span className="bg-pink-500 px-1 rounded" data-id="element-298">LIVE</span>
        </div>
      </div>

      {/* ============ LEFT PANEL: Choose Your Path ============ */}
      <motion.div initial={{
      opacity: 0,
      x: -30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.5
    }} className="absolute left-3 top-[28%] z-40 pointer-events-none" data-id="element-299">
        <div className="bg-black/55 backdrop-blur-md rounded-2xl p-3 border border-white/10" data-id="element-300">
          <div className="text-white text-[10px] font-black tracking-wider mb-2" data-id="element-301">
            CHOOSE YOUR PATH
          </div>
          <div className="space-y-1.5" data-id="element-302">
            {PATH_COLORS.map(c => {
            const Icon = c.icon;
            return <div key={c.name} className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{
              background: `${c.glow}22`,
              border: `1px solid ${c.glow}66`
            }} data-id="element-303">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{
                background: `${c.glow}55`
              }} data-id="element-304">
                    <Icon className="w-3 h-3" style={{
                  color: c.light
                }} data-id="element-305" />
                  </div>
                  <span className="text-xs font-bold" style={{
                color: c.light
              }} data-id="element-306">
                    {c.name}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </motion.div>

      {/* ============ RIGHT SIDE: TikTok-style action rail ============ */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-40 pointer-events-none flex flex-col gap-4 items-center" data-id="element-307">
        {[{
        Icon: HeartIcon,
        label: '2.3K',
        color: 'text-pink-400'
      }, {
        Icon: MessageCircleIcon,
        label: '386',
        color: 'text-white'
      }, {
        Icon: Share2Icon,
        label: '256',
        color: 'text-white'
      }, {
        Icon: GiftIcon,
        label: '',
        color: 'text-pink-400'
      }].map(({
        Icon,
        label,
        color
      }, i) => <div key={i} className="flex flex-col items-center" data-id="element-308">
            <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center" data-id="element-309">
              <Icon className={`w-6 h-6 ${color}`} data-id="element-310" />
            </div>
            {label && <span className="text-white text-[10px] font-bold mt-0.5" data-id="element-311">
                {label}
              </span>}
          </div>)}
      </div>

      {/* ============ BOTTOM: ROLL DICE CTA ============ */}
      <div className="absolute bottom-0 left-0 right-0 z-50 px-4 pb-5 pt-3 bg-gradient-to-t from-black/80 to-transparent" data-id="element-312">
        <motion.button onClick={onStartPlaying} initial={{
        y: 60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 1.2,
        type: 'spring'
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.97
      }} className="w-full max-w-md mx-auto block rounded-2xl py-4 text-center" style={{
        background: 'linear-gradient(180deg, #ff4d8d 0%, #d63385 100%)',
        boxShadow: '0 10px 30px rgba(214,51,133,0.6), inset 0 2px 0 rgba(255,255,255,0.3), inset 0 -3px 0 rgba(0,0,0,0.2)'
      }} data-id="element-313">
          <div className="text-white font-black text-2xl tracking-wide leading-none" data-id="element-314">
            ROLL DICE
          </div>
          <div className="text-white/90 text-xs font-semibold mt-1" data-id="element-315">
            Roll the dice to start your journey!
          </div>
        </motion.button>
      </div>

      {/* ============ BOTTOM-LEFT: Fake comments stream ============ */}
      <div className="absolute left-3 bottom-24 z-40 pointer-events-none space-y-1.5 max-w-[180px]" data-id="element-316">
        {[{
        name: 'QueenB',
        msg: "Let's gooo! 🔥",
        color: 'bg-pink-400'
      }, {
        name: 'MindsetMatters',
        msg: 'Self-made is the move! 💯',
        color: 'bg-purple-400'
      }, {
        name: 'LifeCoachLive',
        msg: 'You got this! 🙌',
        color: 'bg-cyan-400'
      }].map((c, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 1.4 + i * 0.2
      }} className="flex items-center gap-2" data-id="element-317">
            <div className={`w-6 h-6 rounded-full ${c.color} border border-white/40 shrink-0`} data-id="element-318" />
            <div className="text-white text-[11px] leading-tight drop-shadow-md" data-id="element-319">
              <span className="font-bold" data-id="element-320">{c.name}</span>
              <div data-id="element-321">{c.msg}</div>
            </div>
          </motion.div>)}
      </div>
    </div>;
}